import { useCallback, type FC } from 'react';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import Pagination from '@/components/UI/Pagination';
import SearchFilterBar from '@/components/UI/SearchFilterBar';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList, { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import TableShowMenu from '@/components/UI/SelectDropDown/Menus/TableShowMenu';
import Table from '@/components/UI/Table';
import { _isEmpty } from '@/littledash';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';

interface LinksTableState {
  links: Array<Record<string, unknown>>;
  searchQuery: string;
  total_pages: number;
  current_page: number;
  per_page: number;
  order: string;
  sort: string;
  columns: Array<TanStackColumnDef<Record<string, unknown>>>;
  selectedRows: Record<string, boolean>;
}

interface LinksTableProps {
  readOnly?: boolean;
  state: LinksTableState;
  dispatch: (action: { type: string; data?: unknown }) => void;
  addLink: () => void;
  singleRowActions: Array<ActionProps>;
  reduxDispatch: (action: { type: string; setting?: unknown }) => void;
  settings: {
    tables: {
      links: {
        columns: Record<string, boolean>;
      };
    };
  };
  loading: boolean;
}

const LinksTable: FC<LinksTableProps> = ({
  readOnly = false,
  state,
  dispatch,
  addLink,
  singleRowActions,
  reduxDispatch,
  settings,
  loading,
}) => {
  const { links, searchQuery, total_pages, current_page, per_page, order, sort, columns, selectedRows } = state;
  const pagingProps = { total_pages, current_page, per_page };

  const handleSetSelectedRows = useCallback(
    (data: Record<string, boolean>) => dispatch({ type: 'SET_SELECTED', data }),
    [dispatch]
  );
  const handleSetSearchQuery = useCallback(
    (data: string | ((prevState: string) => string)) => {
      const value = typeof data === 'function' ? data(searchQuery) : data;
      dispatch({ type: 'SET_SEARCH_QUERY', data: value });
    },
    [dispatch, searchQuery]
  );
  const handleSetSort = useCallback(
    (data: string | { sort: string; order: string }) => {
      dispatch({ type: 'SET_SORT', data });
    },
    [dispatch]
  );
  const handleSetPaging = useCallback(
    (data: { current_page?: number; per_page?: number }) => dispatch({ type: 'SET_PAGING', data }),
    [dispatch]
  );

  const handleColumnsChange = useCallback(
    (data: Array<TanStackColumnDef<Record<string, unknown>>>) => {
      const newColumns = data.reduce<Record<string, boolean>>((acc, column) => {
        return { ...acc, [column.id as string]: (column as { isVisible?: boolean }).isVisible ?? true };
      }, {});
      const newSettings = {
        ...settings,
        tables: {
          ...settings.tables,
          links: { ...settings.tables.links, columns: newColumns },
        },
      };
      reduxDispatch({ type: 'SAVE_SETTING', setting: newSettings });
      dispatch({ type: 'SET_COLUMNS', data });
    },
    [settings, reduxDispatch, dispatch]
  );

  return (
    <>
      <div className="flex pa3 bb b--moon-gray">
        <div className="flex w-100">
          <div className="flex mr2">
            {!_isEmpty(selectedRows) && (
              <div
                className="mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1"
                style={{
                  whiteSpace: 'nowrap',
                  lineHeight: '2.4rem',
                }}
              >
                {Object.keys(selectedRows).length} selected
              </div>
            )}
            <SelectDropDown
              title="Actions"
              className={`plain f6 ${!_isEmpty(selectedRows) ? 'br--right' : ''}`}
              alignMenu="right"
              disabled={_isEmpty(selectedRows) || Object.keys(selectedRows).length > 1}
              tooltip={Object.keys(selectedRows).length > 1 ? 'Select one link at a time' : undefined}
            >
              <ActionList actions={singleRowActions} />
            </SelectDropDown>
          </div>
          <SearchFilterBar
            canSearch={false}
            className="flex flex-grow-1"
            searchPlaceholderText="Search by file name or type"
            handleSearchInput={handleSetSearchQuery}
            searchQuery={searchQuery}
            ShowMenu={() => (
              <TableShowMenu
                columns={columns as Array<TanStackColumnDef<unknown> & { isVisible?: boolean }>}
                handleChange={
                  handleColumnsChange as (cols: Array<TanStackColumnDef<unknown> & { isVisible?: boolean }>) => void
                }
              />
            )}
          />
          {!readOnly && <Button onClick={addLink}>Add a link</Button>}
        </div>
      </div>
      {loading ? (
        <div className="flex flex-column min-h5 justify-center items-center bg-white">
          <Loading />
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            data={links}
            className="overflow-visible"
            manualSort={{ sort, order }}
            noDataMessage="No links have been added"
            setManualSort={handleSetSort}
            selectedRows={selectedRows}
            setSelectedRows={handleSetSelectedRows}
          />
          <Pagination {...pagingProps} handleChange={handleSetPaging} />
        </>
      )}
    </>
  );
};

export default LinksTable;
